import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Flex,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionIcon,
    AccordionPanel,
    Input,
    ModalFooter,
    Button,
    Text,
    Box,
} from '@chakra-ui/react';
import { SingleValue, ActionMeta } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import AMultiSelect, { MultiSelectOption } from 'components/atoms/AMultiSelect';
import { useGetActionTypeListsQuery, useGetSectorsListsQuery, useUpdateSectoMutation } from 'core/API/sectoApi';
import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import { BDD_COLUMNS } from './const';
import { TSectoViewer } from './SectoContainer';
import { ISecto, ISectoViewData } from './types';

const SectoEditModal = ({
    selectedSecto,
    selectedCustom,
    selectedRetailer,
    options,
    toolId,
    isOpen,
    onClose,
    setSelectedSectoData,
    setSelectedCustoms,
}: TSectoViewer & { toolId: number }) => {
    const companyInfo = useGetCompanyInfoSelector();
    const [updateSectoMutation, { isLoading: isUpdateSectoLoading }] = useUpdateSectoMutation();

    const {
        data: actionType,
        isError: isActionTypeError,
        isLoading: isActionTypeLoading,
    } = useGetActionTypeListsQuery({ idTool: toolId });

    const {
        data: sectorsList,
        isError: isSectorsListError,
        isLoading: isSectorsListLoading,
    } = useGetSectorsListsQuery({ idTool: toolId });

    const onChangeMultiSelect = (key: string) => (selected: MultiSelectOption[]) => {
        const newSelectedSecto = JSON.parse(JSON.stringify(selectedSecto)) as ISecto;

        if (selected) {
            if (!newSelectedSecto.customs) {
                newSelectedSecto.customs = { [key]: { users: selected } };
            } else {
                newSelectedSecto.customs[key].users = selected;
            }
        }

        setSelectedSectoData(newSelectedSecto);
    };

    const onChangeCustomSelect =
        (key: keyof ISectoViewData) =>
        (
            newValue: SingleValue<{
                label: string;
                value: string;
            }>,
            actionMeta: ActionMeta<{
                label: string;
                value: string;
            }>,
        ) => {
            const newSelectedSecto = JSON.parse(JSON.stringify(selectedSecto));
            const column = BDD_COLUMNS[key as keyof typeof BDD_COLUMNS];
            if (column) {
                if (newValue) {
                    if (!newSelectedSecto.customs) {
                        newSelectedSecto.customs = { [key]: { name: newValue.value } };
                    } else if (!newSelectedSecto.customs[key]) {
                        newSelectedSecto.customs[key] = { name: newValue.value };
                    } else {
                        newSelectedSecto.customs[key].name = newValue.value;
                    }
                }
            } else if (newSelectedSecto) {
                switch (key) {
                    case 'id':
                        newSelectedSecto[key] = Number(newValue?.value);
                        break;
                    default:
                        newSelectedSecto[key] = newValue?.value;
                        break;
                }
            }
            const newSelectedCustom: Record<string, { name: string; users: MultiSelectOption[] }> = {};
            if (newSelectedSecto.customs) {
                Object.keys(newSelectedSecto.customs).forEach((key) => {
                    const custom = newSelectedSecto.customs[key];
                    newSelectedCustom[key] = { ...custom };
                });
                setSelectedCustoms(newSelectedCustom);
            }
            setSelectedSectoData(newSelectedSecto as ISecto);
        };

    const submitSectoHandler = async () => {
        const allowed = ['sector_id', 'name', 'shop', 'action_type', 'customs'];

        if (selectedSecto) {
            const filtered = Object.keys(selectedSecto)
                .filter((key) => allowed.includes(key))
                .reduce((obj: any, key) => {
                    obj[key] = selectedSecto[key as keyof ISecto];
                    return obj;
                }, {});
            filtered.shop = selectedSecto.id;

            await updateSectoMutation({
                toolId,
                secto: filtered,
                userData: selectedSecto.customs,
                retailer: selectedRetailer,
                isOverride: false,
            });

            onClose();
        }
    };

    return (
        <Modal size="xl" isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize="2xl">
                        {selectedSecto?.name} ({selectedSecto.id})
                    </Text>
                    <Text fontSize="sm">
                        {`${selectedSecto?.address} - ${selectedSecto?.postcode} ${selectedSecto?.city}`}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Flex flex="1" mb={4}>
                        <FormControl isInvalid={false}>
                            <FormLabel>Action</FormLabel>
                            <CreatableSelect
                                onChange={onChangeCustomSelect('action_type')}
                                value={{
                                    label: selectedSecto.action_type,
                                    value: selectedSecto.action_type,
                                }}
                                options={actionType?.action_types.map((action: string) => {
                                    return {
                                        value: action,
                                        label: action,
                                    };
                                })}
                            />
                            <FormErrorMessage>error</FormErrorMessage>
                        </FormControl>
                    </Flex>
                    <Accordion allowMultiple mb={4}>
                        <AccordionItem>
                            <h2>
                                <AccordionButton>
                                    <Box as="span" flex="1" textAlign="left">
                                        Codes section
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                            </h2>
                            <AccordionPanel pb={4}>
                                <Flex flex="1" mb={4}>
                                    {companyInfo?.send_tdlinx && selectedSecto.td_linx && (
                                        <FormControl mr={4} isInvalid={false}>
                                            <FormLabel>TD Linx</FormLabel>
                                            <Input disabled value={selectedSecto.td_linx} placeholder="name" />
                                            <FormErrorMessage>error</FormErrorMessage>
                                        </FormControl>
                                    )}
                                    <FormControl mr={4} isInvalid={false}>
                                        <FormLabel>Code BB</FormLabel>
                                        <Input disabled value={selectedSecto.code_bb} placeholder="name" />
                                        <FormErrorMessage>error</FormErrorMessage>
                                    </FormControl>
                                    <FormControl mr={4} isInvalid={false}>
                                        <FormLabel>Code Liva</FormLabel>
                                        <Input disabled value={selectedSecto.code_liva} placeholder="name" />
                                        <FormErrorMessage>error</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={false}>
                                        <FormLabel>Code LSA</FormLabel>
                                        <Input disabled value={selectedSecto.code_lsa} placeholder="code_lsa" />
                                        <FormErrorMessage>error</FormErrorMessage>
                                    </FormControl>
                                </Flex>
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>

                    <>
                        {Object.keys(BDD_COLUMNS).map((custom) => {
                            const column = BDD_COLUMNS[custom as keyof typeof BDD_COLUMNS];
                            const customData = selectedCustom[custom];

                            return (
                                <Flex mb={4}>
                                    <FormControl minWidth={150} flex="1" mr={4} isInvalid={false}>
                                        <FormLabel>{column}</FormLabel>
                                        <CreatableSelect
                                            onChange={onChangeCustomSelect(custom)}
                                            value={{ label: customData?.name, value: customData?.name }}
                                            options={
                                                sectorsList &&
                                                sectorsList?.sectors[
                                                    column.split('_')[1] as keyof typeof sectorsList
                                                ]?.map((action: string) => {
                                                    return {
                                                        value: action,
                                                        label: action,
                                                    };
                                                })
                                            }
                                        />
                                        <FormErrorMessage>error</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={false}>
                                        <FormLabel>Responsable</FormLabel>
                                        <AMultiSelect
                                            disabled
                                            selectedOptions={customData?.users}
                                            options={options}
                                            onChange={onChangeMultiSelect(custom)}
                                        />
                                        <FormErrorMessage>error</FormErrorMessage>
                                    </FormControl>
                                </Flex>
                            );
                        })}
                    </>
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Close
                    </Button>
                    <Button isLoading={isUpdateSectoLoading} onClick={submitSectoHandler} colorScheme="blue">
                        Submit
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SectoEditModal;
